// Collapsed-section summary rows (Round 13, Phase 1). Each Overview section
// below "Needs attention" defaults collapsed to a single counted summary row.
// The row deterministically separates ACTIONABLE / time-sensitive counts
// (overdue deadlines, reconciliation discrepancies) from AMBIENT uncertainty
// (an unconfirmed category on a trivial fee, plain reference counts) so that
// "exists" never masquerades as "needs you". Pure functions — unit-tested via
// node --test; no React, no AI.

import type { CommitmentRow } from "./commitments";
import { DEADLINE_SOON_DAYS } from "./needsAttention";

export type SummaryTone = "act" | "amb";

export interface SummaryPart {
  text: string;
  tone: SummaryTone;
}

export interface SectionSummary {
  // How many items in this section need the user. 0 ⇒ render the calm line.
  actionable: number;
  // Ordered display segments (joined by " · " by the renderer).
  parts: SummaryPart[];
  // Quiet line shown when actionable === 0 (e.g. "all current").
  calm?: string;
}

function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function plural(n: number, one: string, many?: string): string {
  return `${n} ${n === 1 ? one : (many ?? `${one}s`)}`;
}

// Reference / ambient sections (current status, chronology, coverage,
// valuation, background): a plain count, never actionable.
export function summarizeCount(
  n: number,
  one: string,
  many?: string,
): SectionSummary {
  return { actionable: 0, parts: [{ text: plural(n, one, many), tone: "amb" }] };
}

// Commitments: overdue (date_passed) and due-soon (pending within the same
// 7-day horizon Needs-attention uses) are ACTIONABLE; remaining open / future
// pending are ambient "open". Nothing actionable ⇒ calm "all current".
export function summarizeCommitments(
  rows: CommitmentRow[],
  todayIso: string,
): SectionSummary {
  const horizon = addDays(todayIso, DEADLINE_SOON_DAYS);
  let overdue = 0;
  let soon = 0;
  let open = 0;
  for (const r of rows) {
    if (r.state === "resolved") continue;
    if (r.state === "date_passed") overdue++;
    else if (
      r.state === "pending" &&
      !!r.fact.deadline &&
      r.fact.deadline.slice(0, 10) <= horizon
    )
      soon++;
    else open++; // open (no deadline) + pending beyond the horizon
  }
  const actionable = overdue + soon;
  const parts: SummaryPart[] = [];
  if (overdue > 0) parts.push({ text: `${overdue} overdue`, tone: "act" });
  if (soon > 0) parts.push({ text: `${soon} due soon`, tone: "act" });
  if (open > 0) parts.push({ text: `${open} open`, tone: "amb" });
  return {
    actionable,
    parts,
    calm: actionable === 0 ? "all current" : undefined,
  };
}

// Financial: a stated-total-that-doesn't-fold reconciliation discrepancy is
// ACTIONABLE (the arithmetic is contradicted). The net state and the
// unconfirmed-category count are AMBIENT — an unconfirmed category on a
// trivial fee is not a task.
export function summarizeFinancial(opts: {
  netLabel: string | null; // formatted net when asserted, else null
  toConfirm: number;
  discrepancies: number;
}): SectionSummary {
  const parts: SummaryPart[] = [];
  if (opts.discrepancies > 0)
    parts.push({
      text: `${plural(opts.discrepancies, "record")} don’t add up`,
      tone: "act",
    });
  parts.push({
    text: opts.netLabel ? `net ${opts.netLabel}` : "total not shown yet",
    tone: "amb",
  });
  if (opts.toConfirm > 0)
    parts.push({ text: `${opts.toConfirm} to confirm`, tone: "amb" });
  return { actionable: opts.discrepancies, parts };
}
